import { format, isValid, parse } from 'date-fns'
import { NoteContextType } from '@dataTypes/note.type'

/**
 * `appt_date` (ISO or date-only) + `appt_start` (24h e.g. "12:00" or 12h with am/pm).
 * Matches call-note style: `MM/dd/yyyy - hh:mm a`.
 */
export function formatAppointmentContextDisplay(
  apptDate: string | Date,
  apptStart?: string | null,
): string | null {
  const parsedDate =
    apptDate instanceof Date ? apptDate : new Date(String(apptDate))
  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  const datePart = format(parsedDate, 'MM/dd/yyyy')
  const start = apptStart?.trim()
  if (!start) {
    return datePart
  }

  const hasAmPm = /\b(am|pm)\b/i.test(start)
  const patterns = hasAmPm
    ? (['h:mm:ss a', 'hh:mm:ss a', 'h:mm a', 'hh:mm a'] as const)
    : (['HH:mm:ss', 'HH:mm', 'H:mm'] as const)

  for (const pattern of patterns) {
    const dt = parse(start, pattern, parsedDate)
    if (isValid(dt)) {
      return format(dt, 'MM/dd/yyyy - hh:mm a')
    }
  }

  return `${datePart} - ${start}`
}

/** Minimal appointment shape for display name lookup (e.g. AppointmentResponse) */
interface AppointmentForDisplay {
  appointment_id: string
  appt_date?: string
  appt_start?: string
  appointment_start_date?: string
  appointment_start_date_time?: string
}

/** Minimal chart/plan shape for display name lookup */
interface ChartOrPlanForDisplay {
  chart_id?: string
  tp_id?: string
  title?: string
  tp_heading?: string
  date_time_created_at?: string
}

export interface NoteFilterContextLookup {
  appointments?: AppointmentForDisplay[]
  perioCharts?: ChartOrPlanForDisplay[]
  dentalCharts?: ChartOrPlanForDisplay[]
  treatmentPlans?: ChartOrPlanForDisplay[]
}

/**
 * Returns a display name for a note filter "item" (context_id + context_type).
 * - Appointment: formatted appointment date (and time if available)
 * - Charting/Perio/Treatment plan: context name (title/heading) or formatted date, else fallback label
 */
export function getContextItemDisplayName(
  contextType: NoteContextType | string,
  contextId: string | null | undefined,
  lookup?: NoteFilterContextLookup,
): string {
  if (!contextId || !contextId.trim()) return 'No Context'

  const ctx = (contextType ?? '').toString()
  const id = contextId.trim()

  if (ctx === NoteContextType.APPOINTMENT || ctx === 'appointment') {
    const appt = lookup?.appointments?.find((a) => a.appointment_id === id)
    if (appt) {
      const dateStr =
        appt.appt_date ??
        appt.appointment_start_date ??
        appt.appointment_start_date_time
      if (dateStr) {
        try {
          const d = new Date(dateStr)
          if (!isNaN(d.getTime())) {
            if (appt.appt_start) {
              const line = formatAppointmentContextDisplay(d, appt.appt_start)
              if (line) return line
            }
            return format(d, 'MM/dd/yyyy - hh:mm a')
          }
        } catch {
          // fall through
        }
      }
    }
    return id
  }

  if (ctx === NoteContextType.CHARTING || ctx === 'charting') {
    const chart =
      lookup?.dentalCharts?.find((c) => c.chart_id === id) ??
      lookup?.perioCharts?.find((c) => c.chart_id === id)
    if (chart?.title) return chart.title
    if (chart?.date_time_created_at) {
      try {
        return format(new Date(chart.date_time_created_at), 'MM/dd/yyyy')
      } catch {
        // ignore
      }
    }
    return `Charting – ${id.slice(0, 8)}`
  }

  if (ctx === NoteContextType.PERIO || ctx === 'perio') {
    const chart = lookup?.perioCharts?.find((c) => c.chart_id === id)
    if (chart?.title) return chart.title
    if (chart?.date_time_created_at) {
      try {
        return format(new Date(chart.date_time_created_at), 'MM/dd/yyyy')
      } catch {
        // ignore
      }
    }
    return `Perio – ${id.slice(0, 8)}`
  }

  if (
    ctx === NoteContextType.TREATMENT_PLAN ||
    ctx === 'treatmentPlan' ||
    ctx === 'treatment_plan'
  ) {
    const tp = lookup?.treatmentPlans?.find((t) => t.tp_id === id)
    if (tp?.tp_heading) return tp.tp_heading
    if (tp?.date_time_created_at) {
      try {
        return format(new Date(tp.date_time_created_at), 'MM/dd/yyyy')
      } catch {
        // ignore
      }
    }
    return `Treatment plan – ${id.slice(0, 8)}`
  }

  return id
}
